import { queryOptions } from "@tanstack/react-query";

import { getAuthHeaders } from "@/hooks/useSofaClient";
import { SofaClientRequestError } from "@/lib/sofaClient";

import type {
  PathsWithMethod,
  ResponseFor,
  SafeParamsFor,
} from "@oai/safe-http-client/types/internal";
import type { StatusCodes } from "@oai/safe-http-client/types/util";
import type { paths, SofaClient } from "@oai/sofa-client";
import type { SofaRequestAuth } from "@/hooks/useSofaClient";

type SofaPaths = paths & object;
type ContentType = "application/json";
type HeadersRecord = Record<string, string | number | boolean | undefined>;

type SofaGetPathname = PathsWithMethod<SofaPaths, "get", "">;
type SofaPostPathname = PathsWithMethod<SofaPaths, "post", "">;

type SofaPostQueryOptions = {
  timeoutMs?: number;
};

export type SofaQueryMethod = "get" | "post";
type SofaPathForMethod<M extends SofaQueryMethod> =
  M extends "get" ? SofaGetPathname : SofaPostPathname;

type SofaParamsForRequest<
  M extends SofaQueryMethod,
  P extends SofaPathForMethod<M>,
> = SafeParamsFor<SofaPaths, M, `${P}`, ContentType>[0];
type SofaResponseForRequest<
  M extends SofaQueryMethod,
  P extends SofaPathForMethod<M>,
> = ResponseFor<SofaPaths, M, `${P}`, ContentType>;
type OperationForRequest<M extends SofaQueryMethod, P extends SofaPathForMethod<M>> =
  NonNullable<SofaPaths[`${P}`]> extends infer PathSpec ?
    PathSpec extends Record<M, unknown> ?
      NonNullable<PathSpec[M]>
    : never
  : never;
type ResponseMapForRequest<M extends SofaQueryMethod, P extends SofaPathForMethod<M>> =
  OperationForRequest<M, P> extends { responses: infer Responses } ? NonNullable<Responses> : never;

type StatusCode2xx =
  StatusCodes extends infer StatusCode ?
    StatusCode extends number ?
      `${StatusCode}` extends `2${string}` ?
        StatusCode
      : never
    : never
  : never;

type DocumentedStatusForRequest<
  M extends SofaQueryMethod,
  P extends SofaPathForMethod<M>,
> = Extract<keyof ResponseMapForRequest<M, P>, StatusCodes>;
type DocumentedOkStatusForRequest<
  M extends SofaQueryMethod,
  P extends SofaPathForMethod<M>,
> = Extract<DocumentedStatusForRequest<M, P>, StatusCode2xx>;
type SuccessfulResponseForRequest<
  M extends SofaQueryMethod,
  P extends SofaPathForMethod<M>,
> = Extract<SofaResponseForRequest<M, P>, { status: DocumentedOkStatusForRequest<M, P> }>;
type SuccessfulDataForRequest<
  M extends SofaQueryMethod,
  P extends SofaPathForMethod<M>,
> = SuccessfulResponseForRequest<M, P>["data"];

type SofaMethodQueryRequest<M extends SofaQueryMethod, P extends SofaPathForMethod<M>> =
  undefined extends SofaParamsForRequest<M, P> ?
    {
      path: P;
      params?: SofaParamsForRequest<M, P>;
    }
  : {
      path: P;
      params: SofaParamsForRequest<M, P>;
    };

export type SofaGetQueryRequest<P extends SofaGetPathname> = SofaMethodQueryRequest<"get", P>;
export type SofaPostQueryRequest<P extends SofaPostPathname> = SofaMethodQueryRequest<"post", P>;

export type SofaQueryRequest<M extends SofaQueryMethod, P extends SofaPathForMethod<M>> = {
  method: M;
} & SofaMethodQueryRequest<M, P>;

export function isStatusOk(status: number): status is StatusCode2xx {
  return status >= 200 && status < 300;
}

function withAuthorizationHeaders<Params extends { headers?: HeadersRecord }>(
  params: Params | undefined,
  auth: SofaRequestAuth,
): Params {
  const newParams = { ...params } as Params;
  newParams.headers = {
    ...(params?.headers ?? {}),
    ...getAuthHeaders(auth),
  };
  return newParams;
}

function buildQueryKey<M extends SofaQueryMethod, P extends SofaPathForMethod<M>>(
  method: M,
  path: P,
  params: SofaParamsForRequest<M, P> | undefined,
  adAccountId: string,
) {
  return [
    "sofa",
    {
      method,
      path,
      params,
      adAccountId,
    },
  ] as const;
}

function assertResponseOk(
  method: string,
  path: string,
  response: { status: number | string | symbol; raw: Response },
) {
  const statusCode =
    typeof response.status === "number" ? response.status : Number(response.status);
  if (!isStatusOk(statusCode)) {
    throw new SofaClientRequestError(
      `API service ${method} ${path} returned HTTP ${String(response.status)}`,
      {
        method,
        path,
        reason: "non_success_status",
        response: response.raw,
      },
    );
  }
}

async function withRequestTimeout<T>(
  querySignal: AbortSignal,
  timeoutMs: number,
  request: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const requestController = new AbortController();
  const abortFromQuery = () => requestController.abort(querySignal.reason);
  if (querySignal.aborted) {
    abortFromQuery();
  } else {
    querySignal.addEventListener("abort", abortFromQuery, { once: true });
  }

  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      const timeoutError = new Error(`Sofa request timed out after ${timeoutMs}ms`);
      timeoutError.name = "TimeoutError";
      requestController.abort(timeoutError);
      reject(timeoutError);
    }, timeoutMs);
  });

  try {
    return await Promise.race([request(requestController.signal), timeoutPromise]);
  } finally {
    if (timeoutId != null) {
      globalThis.clearTimeout(timeoutId);
    }
    querySignal.removeEventListener("abort", abortFromQuery);
  }
}

export function sofaGetQueryOptions<Pathname extends SofaGetPathname>(
  client: SofaClient,
  request: SofaGetQueryRequest<Pathname>,
  auth: SofaRequestAuth,
) {
  type QueryData = SuccessfulDataForRequest<"get", Pathname>;
  const paramsWithAuthorization = withAuthorizationHeaders<SofaParamsForRequest<"get", Pathname>>(
    request.params,
    auth,
  );

  return queryOptions<QueryData>({
    queryKey: buildQueryKey("get", request.path, request.params, auth.adAccountId),
    queryFn: async (): Promise<QueryData> => {
      const get = client.get.bind(client) as (
        path: Pathname,
        params: SafeParamsFor<SofaPaths, "get", `${Pathname}`, ContentType>[0],
      ) => Promise<SofaResponseForRequest<"get", Pathname>>;
      const response = await get(
        request.path,
        paramsWithAuthorization as SafeParamsFor<SofaPaths, "get", `${Pathname}`, ContentType>[0],
      );
      assertResponseOk("GET", request.path, response);

      return response.data as QueryData;
    },
  });
}

export function sofaPostQueryOptions<Pathname extends SofaPostPathname>(
  client: SofaClient,
  request: SofaPostQueryRequest<Pathname>,
  auth: SofaRequestAuth,
  options?: SofaPostQueryOptions,
) {
  type QueryData = SuccessfulDataForRequest<"post", Pathname>;
  const paramsWithAuthorization = withAuthorizationHeaders<SofaParamsForRequest<"post", Pathname>>(
    request.params,
    auth,
  );

  return queryOptions<QueryData>({
    queryKey: buildQueryKey("post", request.path, request.params, auth.adAccountId),
    queryFn: async ({ signal }): Promise<QueryData> => {
      const post = client.post.bind(client) as (
        path: Pathname,
        params: SafeParamsFor<SofaPaths, "post", `${Pathname}`, ContentType>[0],
        request?: RequestInit,
      ) => Promise<SofaResponseForRequest<"post", Pathname>>;
      const params = paramsWithAuthorization as SafeParamsFor<
        SofaPaths,
        "post",
        `${Pathname}`,
        ContentType
      >[0];
      const response =
        options?.timeoutMs == null ?
          await post(request.path, params)
        : await withRequestTimeout(signal, options.timeoutMs, (requestSignal) =>
            post(request.path, params, { signal: requestSignal }),
          );
      assertResponseOk("POST", request.path, response);

      return response.data as QueryData;
    },
  });
}
